import {
  AgentTypeName,
  AGENT_TYPE_PERCENTAGES,
} from '../../components/renditions/agent-type-percentages';

/** 1 = Split Member/Leader, 0 = 100% Team Member */
export type StatisticsComputationMode = 0 | 1;

export const STATISTICS_COMPUTATION_LABELS: Record<
  StatisticsComputationMode,
  string
> = {
  0: '100% Team Member',
  1: 'Split Member/Leader',
};

/** Lo que una persona suma a su estadistica de cierres, en dolares */
export interface StatisticsRow {
  name: string;
  usd: number;
}

export interface StatisticsSummary {
  mode: StatisticsComputationMode;
  rows: StatisticsRow[];
  total: number;
}

export class CommissionSummary {
  // Precio de cierre
  'closedPrice': { usd: number; local: number };
  // Comision Bruta
  'rawCommission': { percentage: number; usd: number; local: number };
  // Referido a cobrar
  'referred': {
    isReferred: boolean;
    isSeller: boolean;
    name: string | null;
    // Deduccion manual
    manualDeduction: { name: string; usd: number; local: number };
    abao: { usd: number; local: number };
    type: { name: string; usd: number; local: number; original: string };
    deduction: { percentage: number; usd: number; local: number };
    firstSubtotal: { usd: number; local: number };
    secondSubtotal: { usd: number; local: number };
    netCommission: { usd: number; local: number };
  };
  // Punta Vendedora
  'seller': {
    officeId: number | null; // si viene != de null es el id de otra oficina
    relation: number;
    percentage: number;
    usd: number;
    local: number;
    otherRealEstateName: string | null; // Usado solo para el caso -> OTRA INMOBILIARIA
    // Agente punta vendedora
    agent: {
      name: string;
      office?: string; // Usado para caso -> OTRO REMAX
      // Deduccion manual
      manualDeduction: { name: string; usd: number; local: number };
      abao: { usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      deduction: { percentage: number; usd: number; local: number };
      firstSubtotal: { usd: number; local: number };
      secondSubtotal: { usd: number; local: number };
      netCommission: { usd: number; local: number };
      // Team Leader
    };
    teamLeader: {
      name: string;
      rawCommission: { usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      agentDeduction: { percentage: number; usd: number; local: number };
      netCommission: { usd: number; local: number };
      captationOrigin: string;
      abao: { usd: number; local: number };
    };
    referred: {
      officeId: number | string | null;
      name: string;
      rawCommission: { percentage: number; usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      agentDeduction: { percentage: number; usd: number; local: number };
      abao: { usd: number; local: number };
      netCommission: { usd: number; local: number };
    };
  };

  //cobrokers
  'cobrokers': {
    id: number;
    name: string;
    office: string | null;
    officeId: number | string | null;
    otherRealEstateName: string | null; // Usado solo para el caso -> OTRA INMOBILIARIA
    rawCommission: { usd: number; local: number };
    type: { name: string; usd: number; local: number; original: string };
    agentDeduction: { percentage: number; usd: number; local: number };
    netCommission: { usd: number; local: number };
    teamLeader: {
      name: string;
      rawCommission: { usd: number; local: number };
      type: {
        name: string;
        usd: number;
        local: number;
        original: string;
      };
      agentDeduction: { percentage: number; usd: number; local: number };
      netCommission: { usd: number; local: number };
    };
  }[];
  // Punta compradora
  'buyer': {
    officeId: number | null;
    relation: number;
    percentage: number;
    usd: number;
    local: number;
    otherRealEstateName: string | null; // Usado solo para el caso -> OTRA INMOBILIARIA
    // Agente punta vendedora
    agent: {
      name: string;
      office?: string; // Usado para caso -> OTRO REMAX
      // Deduccion manual
      manualDeduction: { name: string; usd: number; local: number };
      abao: { usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      deduction: { percentage: number; usd: number; local: number };
      firstSubtotal: { usd: number; local: number };
      secondSubtotal: { usd: number; local: number };
      netCommission: { usd: number; local: number };
      // Team Leader
    };
    teamLeader: {
      name: string;
      rawCommission: { usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      agentDeduction: { percentage: number; usd: number; local: number };
      netCommission: { usd: number; local: number };
      captationOrigin: string;
      abao: { usd: number; local: number };
    };
    referred: {
      officeId: number | string | null;
      name: string;
      rawCommission: { percentage: number; usd: number; local: number };
      type: { name: string; usd: number; local: number; original: string };
      agentDeduction: { percentage: number; usd: number; local: number };
      abao: { usd: number; local: number };
      netCommission: { usd: number; local: number };
    };
  };
  'office': {
    rawCommission: { percentage: number; usd: number; local: number };
    netCommission: { usd: number; local: number };
    previsional: { usd: number; local: number };
    subtotal: { percentage: number; usd: number; local: number };
  };
  'iva': {
    office: { usd: number; local: number };
    agent: { usd: number; local: number };
  };
  // Computo de estadisticas (solo DO: formulario de rendicion y vistas de lectura)
  'statistics': StatisticsSummary;

  constructor() {
    this.closedPrice = { usd: 0, local: 0 };
    this.rawCommission = { percentage: 0, usd: 0, local: 0 };
    this.referred = {
      isReferred: false,
      isSeller: false,
      name: null,
      // Deduccion manual
      manualDeduction: { name: '', usd: 0, local: 0 },
      abao: { usd: 0, local: 0 },
      type: { name: '', usd: 0, local: 0, original: '' },
      deduction: { percentage: 0, usd: 0, local: 0 },
      firstSubtotal: { usd: 0, local: 0 },
      secondSubtotal: { usd: 0, local: 0 },
      netCommission: { usd: 0, local: 0 },
    };
    this.seller = {
      officeId: null,
      relation: 0,
      percentage: 0,
      usd: 0,
      local: 0,
      otherRealEstateName: null,
      agent: {
        name: '',
        office: '',
        manualDeduction: { name: '', usd: 0, local: 0 },
        abao: { usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        deduction: { percentage: 0, usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
        firstSubtotal: { usd: 0, local: 0 },
        secondSubtotal: { usd: 0, local: 0 },
      },
      teamLeader: {
        name: '',
        rawCommission: { usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        agentDeduction: { percentage: 0, usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
        captationOrigin: '',
        abao: { usd: 0, local: 0 },
      },
      referred: {
        officeId: null,
        name: '',
        rawCommission: { percentage: 0, usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        agentDeduction: { percentage: 0, usd: 0, local: 0 },
        abao: { usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
      },
    };

    this.cobrokers = [];

    this.buyer = {
      officeId: null,
      relation: 0,
      percentage: 0,
      usd: 0,
      local: 0,
      otherRealEstateName: null,
      agent: {
        name: '',
        office: '',
        manualDeduction: { name: '', usd: 0, local: 0 },
        abao: { usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        deduction: { percentage: 0, usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
        firstSubtotal: { usd: 0, local: 0 },
        secondSubtotal: { usd: 0, local: 0 },
      },
      teamLeader: {
        name: '',
        rawCommission: { usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        agentDeduction: { percentage: 0, usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
        captationOrigin: '',
        abao: { usd: 0, local: 0 },
      },
      referred: {
        officeId: null,
        name: '',
        rawCommission: { percentage: 0, usd: 0, local: 0 },
        type: { name: '', usd: 0, local: 0, original: '' },
        agentDeduction: { percentage: 0, usd: 0, local: 0 },
        abao: { usd: 0, local: 0 },
        netCommission: { usd: 0, local: 0 },
      },
    };
    this.office = {
      rawCommission: { percentage: 0, usd: 0, local: 0 },
      subtotal: { percentage: 0, usd: 0, local: 0 },
      netCommission: { usd: 0, local: 0 },
      previsional: { usd: 0, local: 0 },
    };
    this.iva = {
      office: { usd: 0, local: 0 },
      agent: { usd: 0, local: 0 },
    };
    this.statistics = { mode: 1, rows: [], total: 0 };
  }
  public static readonly dataArray: Record<string, number> =
    AGENT_TYPE_PERCENTAGES;

  referidoACobrarCalculation() {
    // Subtotales AGENTE vendedor
    this.referred.firstSubtotal.usd =
      (this.closedPrice.usd ?? 0) - (this.referred.manualDeduction.usd ?? 0);
    this.referred.firstSubtotal.local =
      (this.closedPrice.local ?? 0) -
      (this.referred.manualDeduction.local ?? 0);
    // Subtotales AGENTE vendedor
    this.referred.secondSubtotal.usd = this.referred.firstSubtotal.usd;
    // (this.seller.teamLeader.rawCommission.usd ?? 0);
    this.referred.secondSubtotal.local = this.referred.firstSubtotal.local;
    // (this.referred.teamLeader.rawCommission.local ?? 0);
    // Subtotales AGENTE vendedor
    // this.seller.agent.secondSubtotal.usd = this.seller.agent.firstSubtotal.usd;
    // this.seller.agent.secondSubtotal.local =
    //   this.seller.agent.firstSubtotal.local;

    // Comision Neta AGENTE vendedor
    this.referred.netCommission.usd =
      this.referred.secondSubtotal.usd -
      (this.referred.type.usd ?? 0) -
      (this.referred.deduction?.usd ?? 0) -
      (this.referred.abao?.usd ?? 0);
    // Comision neta AGENTE vendedor
    this.referred.netCommission.local =
      this.referred.secondSubtotal.local -
      (this.referred.type.local ?? 0) -
      (this.referred.deduction?.local ?? 0) -
      (this.referred.abao?.local ?? 0);
  }

  sellerAgentCalculation() {
    // Subtotales AGENTE vendedor
    this.seller.agent.firstSubtotal.usd =
      (this.seller.usd ?? 0) -
      (this.seller.agent.manualDeduction.usd ?? 0) -
      (this.seller.referred.rawCommission.usd ?? 0);
    this.seller.agent.firstSubtotal.local =
      (this.seller.local ?? 0) -
      (this.seller.agent.manualDeduction.local ?? 0) -
      (this.seller.referred.rawCommission.local ?? 0);

    // Subtotales AGENTE vendedor
    this.seller.agent.secondSubtotal.usd =
      this.seller.agent.firstSubtotal.usd -
      (this.seller.teamLeader.rawCommission.usd ?? 0);
    this.seller.agent.secondSubtotal.local =
      this.seller.agent.firstSubtotal.local -
      (this.seller.teamLeader.rawCommission.local ?? 0);

    // Comision Neta AGENTE vendedor
    this.seller.agent.netCommission.usd =
      this.seller.agent.secondSubtotal.usd -
      (this.seller.agent.type.usd ?? 0) -
      (this.seller.agent.deduction?.usd ?? 0) -
      (this.seller.agent.abao?.usd ?? 0);
    // Comision neta AGENTE vendedor
    this.seller.agent.netCommission.local =
      this.seller.agent.secondSubtotal.local -
      (this.seller.agent.type.local ?? 0) -
      (this.seller.agent.deduction?.local ?? 0) -
      (this.seller.agent.abao?.local ?? 0);
  }

  sellerReferredCalculation() {
    this.seller.referred.netCommission.usd =
      (this.seller.referred.rawCommission.usd ?? 0) -
      (this.seller.referred.type.usd ?? 0) -
      (this.seller.referred.agentDeduction.usd ?? 0) -
      (this.seller.referred.abao.usd ?? 0);
    this.seller.referred.netCommission.local =
      (this.seller.referred.rawCommission.local ?? 0) -
      (this.seller.referred.type.local ?? 0) -
      (this.seller.referred.agentDeduction.local ?? 0) -
      (this.seller.referred.abao.local ?? 0);
  }

  sellerTeamLeaderCalculation() {
    this.seller.teamLeader.netCommission.usd =
      (this.seller.teamLeader.rawCommission.usd || 0) -
      (this.seller.teamLeader.type.usd || 0) -
      (this.seller.teamLeader.abao.usd || 0) -
      (this.seller.teamLeader.agentDeduction.usd || 0);
    this.seller.teamLeader.netCommission.local =
      (this.seller.teamLeader.rawCommission.local || 0) -
      (this.seller.teamLeader.type.local || 0) -
      (this.seller.teamLeader.abao.local || 0) -
      (this.seller.teamLeader.agentDeduction.local || 0);
  }

  buyerAgentCalculation() {
    // Subtotales AGENTE vendedor
    this.buyer.agent.firstSubtotal.usd =
      (this.buyer.usd ?? 0) -
      (this.buyer.agent.manualDeduction.usd ?? 0) -
      (this.buyer.referred.rawCommission.usd ?? 0);
    this.buyer.agent.firstSubtotal.local =
      (this.buyer.local ?? 0) -
      (this.buyer.agent.manualDeduction.local ?? 0) -
      (this.buyer.referred.rawCommission.local ?? 0);
    // Subtotales AGENTE vendedor
    this.buyer.agent.secondSubtotal.usd =
      this.buyer.agent.firstSubtotal.usd -
      (this.buyer.teamLeader.rawCommission.usd ?? 0);
    this.buyer.agent.secondSubtotal.local =
      this.buyer.agent.firstSubtotal.local -
      (this.buyer.teamLeader.rawCommission.local ?? 0);

    // Comision Neta AGENTE vendedor
    this.buyer.agent.netCommission.usd =
      this.buyer.agent.secondSubtotal.usd -
      (this.buyer.agent.type.usd ?? 0) -
      (this.buyer.agent.deduction?.usd ?? 0) -
      (this.buyer.agent.abao?.usd ?? 0);
    // Comision neta AGENTE vendedor
    this.buyer.agent.netCommission.local =
      this.buyer.agent.secondSubtotal.local -
      (this.buyer.agent.type.local ?? 0) -
      (this.buyer.agent.deduction?.local ?? 0) -
      (this.buyer.agent.abao?.local ?? 0);
  }

  buyerReferredCalculation() {
    this.buyer.referred.netCommission.usd =
      (this.buyer.referred.rawCommission.usd ?? 0) -
      (this.buyer.referred.type.usd ?? 0) -
      (this.buyer.referred.agentDeduction.usd ?? 0) -
      (this.buyer.referred.abao.usd ?? 0);
    this.buyer.referred.netCommission.local =
      (this.buyer.referred.rawCommission.local ?? 0) -
      (this.buyer.referred.type.local ?? 0) -
      (this.buyer.referred.agentDeduction.local ?? 0) -
      (this.buyer.referred.abao.local ?? 0);
  }

  buyerTeamLeaderCalculation() {
    this.buyer.teamLeader.netCommission.usd =
      (this.buyer.teamLeader.rawCommission.usd || 0) -
      (this.buyer.teamLeader.type.usd || 0) -
      (this.buyer.teamLeader.abao.usd || 0) -
      (this.buyer.teamLeader.agentDeduction.usd || 0);
    this.buyer.teamLeader.netCommission.local =
      (this.buyer.teamLeader.rawCommission.local || 0) -
      (this.buyer.teamLeader.type.local || 0) -
      (this.buyer.teamLeader.abao.local || 0) -
      (this.buyer.teamLeader.agentDeduction.local || 0);
  }

  officeCalculation() {
    // === NUEVO: totales de cobrokers (punta vendedora) ===
    const cobrokers = this.cobrokers ?? [];

    const COBROKERS_OWN_OFFICE = cobrokers.filter(
      (c) => !c.officeId && c.office
    );
    const COBROKERS_EXTERNAL = cobrokers.filter(
      (c) => (c.officeId && c.office) || c.otherRealEstateName
    );

    const COBROKERS_USD_OWN = COBROKERS_OWN_OFFICE.reduce((sum, c) => {
      const cobrokerNet = c.netCommission?.usd ?? 0;
      const teamLeaderNet = c.teamLeader?.netCommission?.usd ?? 0;
      return sum + cobrokerNet + teamLeaderNet;
    }, 0);

    const COBROKERS_USD_EXTERNAL = COBROKERS_EXTERNAL.reduce((sum, c) => {
      return isNaN(sum) ? 0 : sum + c.rawCommission.usd ;
    }, 0);



    const COBROKERS_LOCAL_OWN = COBROKERS_OWN_OFFICE.reduce((sum, c) => {
      const cobrokerNet = c.netCommission?.local ?? 0;
      const teamLeaderNet = c.teamLeader?.netCommission?.local ?? 0;
      return sum + cobrokerNet + teamLeaderNet;
    }, 0);

    const COBROKERS_LOCAL_EXTERNAL = COBROKERS_EXTERNAL.reduce((sum, c) => {
      return isNaN(sum) ? 0 : sum + c.rawCommission.local;
    }, 0);

    // Si tengo ambas puntas de mi oficina
    if (
      (this.seller.officeId == null || this.seller.officeId == 0) &&
      this.seller.otherRealEstateName == null &&
      (this.buyer.officeId == null || this.buyer.officeId == 0) &&
      this.buyer.otherRealEstateName == null
    ) {
      this.office.rawCommission.usd =
        this.rawCommission.usd - COBROKERS_USD_EXTERNAL;
      this.office.rawCommission.local =
        this.rawCommission.local - COBROKERS_LOCAL_EXTERNAL;

      this.office.subtotal.usd = this.office.rawCommission.usd;
      this.office.subtotal.local = this.office.rawCommission.local;

      if (this.seller.referred.officeId) {
        this.office.subtotal.usd =
          this.office.subtotal.usd - this.seller.referred.rawCommission.usd;
        this.office.subtotal.local =
          this.office.subtotal.local - this.seller.referred.rawCommission.local;
      }

      if (this.buyer.referred.officeId) {
        this.office.subtotal.usd =
          this.office.subtotal.usd - this.buyer.referred.rawCommission.usd;
        this.office.subtotal.local =
          this.office.subtotal.local - this.buyer.referred.rawCommission.local;
      }

      this.office.netCommission.usd =
        this.office.rawCommission.usd -
        (this.seller.agent.netCommission.usd ?? 0) -
        (this.seller.referred.netCommission.usd ?? 0) -
        (this.seller.teamLeader.netCommission.usd ?? 0) -
        (this.buyer.agent.netCommission.usd ?? 0) -
        (this.buyer.referred.netCommission.usd ?? 0) -
        (this.buyer.teamLeader.netCommission.usd ?? 0) -
        // NUEVO: lo que se paga a cobrokers (vendedora)
        COBROKERS_USD_OWN;

      this.office.netCommission.local =
        this.office.rawCommission.local -
        (this.seller.agent.netCommission.local ?? 0) -
        (this.seller.referred.netCommission.local ?? 0) -
        (this.seller.teamLeader.netCommission.local ?? 0) -
        (this.buyer.agent.netCommission.local ?? 0) -
        (this.buyer.referred.netCommission.local ?? 0) -
        (this.buyer.teamLeader.netCommission.local ?? 0) -
        // NUEVO: lo que se paga a cobrokers (vendedora)
        COBROKERS_LOCAL_OWN;
    } else {
      // Si la punta vendedora NO es de mi oficina
      if (
        (this.seller.officeId != null && this.seller.officeId != 0) ||
        this.seller.otherRealEstateName
      ) {
        const buyerReferredUsd =
          this.buyer.referred.officeId != null
            ? this.buyer.referred.rawCommission.usd
            : 0;
        const buyerReferredLocal =
          this.buyer.referred.officeId != null
            ? this.buyer.referred.rawCommission.local
            : 0;

        const sellerReferredUsd =
          this.seller.referred.officeId != null
            ? this.seller.referred.rawCommission.usd
            : 0;
        const sellerReferredLocal =
          this.seller.referred.officeId != null
            ? this.seller.referred.rawCommission.local
            : 0;

        this.office.rawCommission.usd =
          this.rawCommission.usd -
          this.seller.agent.netCommission.usd -
          buyerReferredUsd -
          sellerReferredUsd
        //  - COBROKERS_USD_EXTERNAL;
        this.office.rawCommission.local =
          this.rawCommission.local -
          this.seller.agent.netCommission.local -
          buyerReferredLocal -
          sellerReferredLocal 
          //- COBROKERS_LOCAL_EXTERNAL;

        this.office.netCommission.usd =
          this.office.rawCommission.usd -
          (this.buyer.agent.netCommission.usd ?? 0) -
          (this.buyer.referred.officeId
            ? 0
            : this.buyer.referred.netCommission.usd) -
          (this.buyer.teamLeader.netCommission.usd ?? 0) -
          // NUEVO: cobrokers punta vendedora (si por alguna razón existen igual)
          COBROKERS_USD_OWN;

        this.office.netCommission.local =
          this.office.rawCommission.local -
          (this.buyer.agent.netCommission.local ?? 0) -
          (this.buyer.referred.officeId
            ? 0
            : this.buyer.referred.netCommission.local) -
          (this.buyer.teamLeader.netCommission.local ?? 0) -
          // NUEVO: cobrokers punta vendedora
          COBROKERS_LOCAL_OWN;
      }

      // Si la punta compradora NO es de mi oficina
      if (
        (this.buyer.officeId != null && this.buyer.officeId != 0) ||
        this.buyer.otherRealEstateName
      ) {
        const sellerReferredUsd =
          this.seller.referred.officeId != null
            ? this.seller.referred.rawCommission.usd
            : 0;
        const sellerReferredLocal =
          this.seller.referred.officeId != null
            ? this.seller.referred.rawCommission.local
            : 0;

        const buyerReferredUsd =
          this.buyer.referred.officeId != null
            ? this.buyer.referred.rawCommission.usd
            : 0;
        const buyerReferredLocal =
          this.buyer.referred.officeId != null
            ? this.buyer.referred.rawCommission.local
            : 0;

        this.office.rawCommission.usd =
          this.rawCommission.usd -
          this.buyer.usd -
          buyerReferredUsd -
          sellerReferredUsd -
          COBROKERS_USD_EXTERNAL;
        this.office.rawCommission.local =
          this.rawCommission.local -
          this.buyer.local -
          buyerReferredLocal -
          sellerReferredLocal -
          COBROKERS_LOCAL_EXTERNAL;

        this.office.netCommission.usd =
          this.office.rawCommission.usd -
          (this.seller.agent.netCommission.usd ?? 0) -
          (this.seller.referred.officeId
            ? 0
            : this.seller.referred.netCommission.usd) -
          (this.seller.teamLeader.netCommission.usd ?? 0) -
          // NUEVO: cobrokers punta vendedora
          COBROKERS_USD_OWN;

        this.office.netCommission.local =
          this.office.rawCommission.local -
          (this.seller.agent.netCommission.local ?? 0) -
          (this.seller.referred.officeId
            ? 0
            : this.seller.referred.netCommission.local) -
          (this.seller.teamLeader.netCommission.local ?? 0) -
          // NUEVO: cobrokers punta vendedora
          COBROKERS_LOCAL_OWN;
      }

      this.office.subtotal.local = this.office.rawCommission.local;
      this.office.subtotal.percentage = this.office.rawCommission.percentage;
      this.office.subtotal.usd = this.office.rawCommission.usd;
    }
  }
}
